import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState, MobileRow } from "@/components/Shared";
import { WorkspaceCurrencyCard } from "@/components/WorkspaceCurrencyCard";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";

export default function Members() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = () => api
    .get("/users")
    .then(({ data }) => setRows(data))
    .catch(() => setError("This page is restricted to administrators."));

  useEffect(() => { load(); }, []);

  const clearPictures = async (id) => {
    try {
      await api.delete(`/users/${id}/pictures`);
      await load();
      toast.success("Pictures removed");
    } catch {
      toast.error("Could not remove those pictures");
    }
  };

  const sendReset = async (id, email) => {
    try {
      const { data } = await api.post(`/security/users/${id}/reset-link`, {});
      toast.success(`Reset link sent to ${data.email} — it expires in ${data.expires_in_minutes} minutes`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="members-page">
      <PageHeader overline="Administration" title="Members" description="Everyone with an account on this workspace." />
      {error ? (
        <EmptyState testid="members-forbidden" title="Not allowed" description={error} />
      ) : (
        <>
          <WorkspaceCurrencyCard />
          <div className="border border-border bg-white md:hidden" data-testid="members-cards">
            {rows.map((u) => (
              <MobileRow
                key={u.id}
                testid={`member-card-${u.id}`}
                title={<span className="flex items-center gap-2"><Avatar imageId={u.avatar_id} name={u.name} size="sm" testid={`member-card-avatar-${u.id}`} />{u.name}</span>}
                badges={<StatusBadge value={u.role} />}
                fields={[
                  { label: "Email", value: u.email },
                  { label: "Company", value: u.company || "—" },
                  { label: "Joined", value: (u.created_at || "").slice(0, 10) },
                ]}
                actions={
                  <button
                    data-testid={`send-reset-card-${u.id}`}
                    onClick={() => sendReset(u.id, u.email)}
                    className="tap-safe border border-border px-2 py-1 text-[0.65rem] uppercase tracking-wider text-[#002FA7]"
                  >
                    Send reset link
                  </button>
                }
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
          <table className="w-full min-w-[600px] text-sm" data-testid="members-table">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left">
                {["Name", "Email", "Role", "Company", "Joined", "Pictures", "Password"].map((h) => (
                  <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} data-testid={`member-row-${u.id}`} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-[#0F172A]">
                    <span className="flex items-center gap-2">
                      <Avatar imageId={u.avatar_id} name={u.name} size="sm" testid={`member-avatar-${u.id}`} />
                      {u.name}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{u.email}</td>
                  <td className="px-3 py-2.5"><StatusBadge value={u.role} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{u.company || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {(u.avatar_id || u.cover_id || u.logo_id || u.company_cover_id) ? (
                      <button
                        data-testid={`clear-pictures-${u.id}`}
                        onClick={() => clearPictures(u.id)}
                        className="border border-border px-2 py-1 text-[0.65rem] uppercase tracking-wider text-[#B3241B] transition-colors duration-150 hover:bg-slate-100"
                      >
                        Remove
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <button
                      data-testid={`send-reset-${u.id}`}
                      onClick={() => sendReset(u.id, u.email)}
                      className="border border-border px-2 py-1 text-[0.65rem] uppercase tracking-wider text-[#002FA7] transition-colors duration-150 hover:bg-slate-100"
                    >
                      Send reset link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
